import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { formatDate } from '~/shared/utils/formatNumber';
import { OptLogVolumeActions } from '~/shared/types/api';
import { Account } from '~/shared/utils/types';
import SizeCell from '~/shared/components/SizeCell';
import { SkeletonCellRenderer } from '~/shared/components/Table/SkeletonCellRenderer';
import Typography from '~/shared/components/Typography';
import { getActionActorKey } from '~/shared/utils/actionActor';

import VolumeIdCell from './VolumeIdCell';
import TypeCell from './TypeCell';
import ActionActorCell from './ActionActorCell';

type OptLogVolumeActionsWithSkeleton = OptLogVolumeActions & { _isSkeleton?: boolean };

export function getActionByColumnDef(): ColDef<OptLogVolumeActionsWithSkeleton> {
  return {
    headerName: 'By',
    colId: 'by',
    sortable: false,
    width: 220,
    minWidth: 200,
    flex: 1,
    suppressSizeToFit: true,
    cellRenderer: ({ data, node }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
      if (data?._isSkeleton) {
        return <SkeletonCellRenderer width={100} />;
      }
      if (!data?.type) {
        return null;
      }

      const rowKey = `${getActionActorKey(data)}-${node.rowIndex ?? 'row'}`;
      return <ActionActorCell action={data} rowKey={rowKey} />;
    },
  };
}

export const getOperationColumnDefs = ({
  sortField,
  isOrgView,
  accounts,
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
  isOrgView?: boolean;
  accounts?: Account[];
}): ColDef<OptLogVolumeActionsWithSkeleton>[] => {
  const columns: ColDef<OptLogVolumeActions>[] = [
    {
      headerName: 'Volume ID',
      width: 260,
      minWidth: 260,
      suppressSizeToFit: true,
      field: 'volumeId',
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={200} />;
        }
        return data?.volumeId ? (
          <VolumeIdCell id={data.volumeId} accountId={isOrgView ? data.accountId : undefined} />
        ) : null;
      },
      sort: sortField?.field === 'volumeId' ? sortField?.dir : null,
    },
    {
      headerName: 'Instance ID',
      width: 200,
      minWidth: 200,
      suppressSizeToFit: true,
      field: 'instanceId',
      sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
      cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={150} />;
        }
        return data?.instanceId || null;
      },
    },
    {
      headerName: 'Type',
      width: 300,
      minWidth: 300,
      // maxWidth: 300,
      suppressSizeToFit: true,
      field: 'type',
      cellRenderer: (params: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        const { data, node } = params;
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={200} />;
        }
        if (!data?.type) {
          return null;
        }
        const uniqueId = `${data.volumeId || 'vol'}-${data.actionFinishTime || node.rowIndex}`;
        return (
          <TypeCell
            key={uniqueId}
            type={data.type}
            volumeId={uniqueId}
            progress={data.progress}
            state={data.state}
          />
        );
      },
    },

    {
      headerName: 'Original Size',
      width: 135,
      minWidth: 135,
      suppressSizeToFit: true,
      field: 'originalDiskSizeGb',
      // valueFormatter: ({ value }) => `${value} GiB`,
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={100} />;
        }
        return data?.originalDiskSizeGb ? (
          <SizeCell size={data.originalDiskSizeGb} sizeInGiB />
        ) : null;
      },
      sort: sortField?.field === 'originalDiskSizeGb' ? sortField?.dir : null,
    },
    {
      headerName: 'New Size',
      width: 135,
      minWidth: 135,
      suppressSizeToFit: true,
      field: 'newDiskSizeGb',
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if ((data as OptLogVolumeActionsWithSkeleton)?._isSkeleton) {
          return <SkeletonCellRenderer width={100} />;
        }
        return data?.newDiskSizeGb ? <SizeCell size={data.newDiskSizeGb} sizeInGiB /> : null;
      },
      sort: sortField?.field === 'newDiskSizeGb' ? sortField?.dir : null,
    },
    {
      headerName: 'Start Time',
      width: 160,
      minWidth: 160,
      suppressSizeToFit: true,
      field: 'actionStartTime',
      cellRenderer: ({ data, value }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={120} />;
        }
        return value ? formatDate(value, true) : null;
      },
      sort: sortField?.field === 'actionStartTime' ? sortField?.dir : null,
      cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
    },
    {
      headerName: 'End Time',
      width: 160,
      minWidth: 160,
      suppressSizeToFit: true,
      field: 'actionFinishTime',
      cellRenderer: ({ data, value }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={120} />;
        }
        return data?.state && ['Success', 'Failure', 'Canceled'].includes(data.state)
          ? formatDate(value, true)
          : null;
      },
      sort: sortField?.field === 'actionFinishTime' ? sortField?.dir : null,
      cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
    },
  ];
  if (isOrgView && accounts) {
    columns.push({
      headerName: 'Account',
      width: 120,
      minWidth: 120,
      suppressSizeToFit: true,
      field: 'accountId',
      sortable: false,
      cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActionsWithSkeleton>) => {
        if (data?._isSkeleton) {
          return <SkeletonCellRenderer width={80} />;
        }

        const value = data?.accountId;
        const account = accounts?.find((acc) => acc.accountId === value);
        const displayName = account?.accountName || value || '-';

        return (
          <div className="flex items-center justify-start w-full">
            <Typography
              variant="p3"
              fontWeight={500}
              className="text-[14px] text-[#071031] text-left"
            >
              {displayName}
            </Typography>
          </div>
        );
      },
    });
  }

  columns.push(getActionByColumnDef());

  return columns;
};
